import React, { useEffect, useRef } from "react";
import { Button } from "@/shared/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEnhancedUserProfile } from "@/lib/hooks/use-enhanced-user-profile";
import { NavUser } from "@/features/sidebar/nav-user";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { SearchInput } from "@/features/sidebar/search-input";
import { SearchDropdown } from "@/features/sidebar/search-dropdown";
import { useSearch } from "@/lib/hooks/use-search";
import { UserDropdown } from "@/features/header/user-dropdown";

export function SiteHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Check if current page is search page
  const isSearchPage = location.pathname === "/search";

  // Search state
  const {
    query,
    setQuery,
    users,
    works,
    isLoading,
    isOpen,
    setIsOpen,
    clearSearch,
  } = useSearch();

  // Get the user profile data from the enhanced hook - but only use it if user exists
  const { isAuthenticated, userData, handleSignOut } = useEnhancedUserProfile();

  // For logged-out users, override the auth state to prevent loading issues
  const actuallyAuthenticated = user && isAuthenticated;
  const actualUserData = user ? userData : null;

  // Make sure the userData is properly constructed
  const validUserData =
    actualUserData && actualUserData.username !== "Loading..."
      ? actualUserData
      : null;

  // Check if we're ready to show the user profile
  const showUserProfile = actuallyAuthenticated && validUserData;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('input[type="text"]')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  // Handle search input focus - only open dropdown if there's query text
  const handleSearchFocus = () => {
    if (query.trim().length > 0) {
      setIsOpen(true);
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setQuery(value);
    // Show dropdown only if there's text in the search box
    setIsOpen(value.trim().length > 0);
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    // Delayed to allow clicking on search results
    setTimeout(() => {
      if (document.activeElement?.tagName !== "A") {
        setIsOpen(false);
      }
    }, 150);
  };

  // Handle Enter key in search
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    // Navigate to search page on Enter
    if (e.key === "Enter" && query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
    }
    // Close dropdown on Escape
    else if (e.key === "Escape") {
      setIsOpen(false);
      (e.target as HTMLElement).blur();
    }
  };

  // Clear search handler
  const handleClearSearch = () => {
    clearSearch();
    setIsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white px-1 md:px-0 overscroll-behavior-y-none">
      {/* Desktop Header */}
      <div className="h-[--header-height] flex w-full items-center gap-2 px-2 md:px-4">
        <div className="flex items-center">
          {/* Logo - now just links to home */}
          <Link
            to="/"
            className="flex flex-row items-center gap-2 hover:opacity-90 hover:scale-95 transition-scale duration-200"
          >
            <img
              src="/assets/logo/gallery.svg"
              alt="Gallery Logo"
              className="h-[22px] md:h-[24px] opacity-100 hover:opacity-95 transition-opacity"
            />
          </Link>
        </div>

        {/* Center area with search - show for authenticated users */}
        <div className="flex-1 flex justify-center relative">
          {actuallyAuthenticated && (
            <div
              ref={searchContainerRef}
              className="w-full max-w-md mx-auto px-2 md:mx-4 md:pl-2"
            >
              <SearchInput
                value={query}
                onChange={handleSearchChange}
                onClear={handleClearSearch}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
                isLoading={isLoading}
                placeholder="Discover Gallery..."
              />

              {/* Search Dropdown positioned relative to container */}
              <div ref={dropdownRef} className="relative">
                <SearchDropdown
                  isOpen={isOpen && query.trim().length > 0}
                  isLoading={isLoading}
                  users={users}
                  works={works}
                  onClose={() => setIsOpen(false)}
                  searchQuery={query}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {actuallyAuthenticated ? (
            // When authenticated, wrap nav-user in dropdown
            showUserProfile ? (
              <UserDropdown>
                <div className="cursor-pointer">
                  <NavUser
                    user={validUserData}
                    onSignOut={handleSignOut}
                    variant="header"
                    compactOnMobile={true}
                  />
                </div>
              </UserDropdown>
            ) : (
              <div className="h-[38px] w-[38px] md:w-28 animate-pulse bg-light rounded-xl"></div>
            )
          ) : (
            <>
              <Link to="/login" className="">
                <Button
                  variant="ghost"
                  className="rounded-full text-dark hover:text-black px-2"
                >
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="default" className="rounded-full px-6">
                  Join for free
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
